import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons';
import { MarketplaceEmailSender } from '@spike/email-marketing-model';
import AlertMessage from 'components/UI/V3/AlertMessage/AlertMessage';
import Button from 'components/UI/V3/Button/Button';
import { FunctionComponent } from 'react';
import EmailSenderSectionHeader from '../EmailSenderSectionHeader';
import { useEmailSenderData } from '../hooks/useEmailSenderData';
import { SHOW_CUDDLES_DEFAULT_ESCAPE } from './senderFeatureFlags';
import sharedStyles from './states.module.scss';

interface FailedStateProps {
    sender: MarketplaceEmailSender;
    /** Opens the edit modal pre-filled with the failed address. */
    onEdit: () => void;
    /** Opens the "Use Cuddles default?" confirm modal. */
    onEscape: () => void;
}

/**
 * Verification failed — SES rejected the address (invalid format, blocked
 * domain, conflict, etc.). The user must correct the address rather than
 * retry the same input, so the only action inside the alert is **Edit
 * Email**. The escape link below the card offers a way out for users who
 * decide they don't want a custom sender after all.
 */
const FailedState: FunctionComponent<FailedStateProps> = ({ sender, onEdit, onEscape }) => {
    const { saving } = useEmailSenderData();

    return (
        <div className={sharedStyles.container}>
            <EmailSenderSectionHeader />

            <div className={sharedStyles.content}>
                <div className={sharedStyles.emailRow}>
                    <p className={sharedStyles.email}>{sender.senderEmail}</p>
                    <span className={`${sharedStyles.badge} ${sharedStyles.badgeFailed}`}>Verification failed</span>
                </div>

                <AlertMessage
                    icon={faCircleXmark}
                    bgColor="rgba(239, 79, 87, 0.08)"
                    bdColor="#ef4f57"
                    iconColor="#ef4f57"
                >
                    <span>
                        {sender.lastError ||
                            'We couldn’t verify this address. Please check it and try a different one.'}
                    </span>
                    <div className={sharedStyles.buttonsInsideAlert}>
                        <Button
                            size="medium"
                            label="Edit Email"
                            variant="black"
                            onClick={onEdit}
                            disabled={saving}
                        />
                    </div>
                </AlertMessage>
            </div>

            {SHOW_CUDDLES_DEFAULT_ESCAPE && (
                <p className={sharedStyles.escapeLine}>
                    Don&apos;t want a custom sender?{' '}
                    <button
                        type="button"
                        className={sharedStyles.escapeLink}
                        onClick={onEscape}
                        disabled={saving}
                    >
                        Use the Cuddles default instead
                    </button>
                </p>
            )}
        </div>
    );
};

export default FailedState;
